'use client'

import React, { useMemo } from 'react'

import { THEME_COLORS_ENUM } from '../../../types/colors'
import { MButton, M_BUTTON_ARROW_ENUM, M_BUTTON_VARIANT_ENUM } from '../../button'

export interface IProgramCardFooterProps {
	programId: number
	/** Order button link. Button is shown only when href is provided. */
	href?: string
	/** Order button theme color */
	color?: THEME_COLORS_ENUM
	/** Price shown in button label */
	price?: number
	/** Old price shown in button label when set (e.g. "bolo X €") */
	oldPrice?: number
	/** Optional base URL of SVG sprite for arrow icon (e.g. "/assets/layout/icons.svg"). When set and showOrderArrow is true, order button shows arrow right icon. */
	icon?: string
	/** When true (default), show arrow icon on the order button. Set false e.g. in program preview. */
	showOrderArrow?: boolean
	/** Link component for order button (e.g. next/link, <a>). Defaults to <a> via MButton. */
	link?: React.ElementType<React.AnchorHTMLAttributes<HTMLAnchorElement>>
	/** Order button label. Use {price} as placeholder for formatted price. When set, overrides the default label. */
	orderButtonLabel?: string
	/** When set, show "Pridať voliteľné moduly" button (outline) linking to this href (e.g. "#kupit-program"). */
	modulesHref?: string
}

function formatPrice(value: number): string {
	return `${value.toFixed(2).replace('.', ',')} €`
}

function formatOrderLabel(template: string, price: number | undefined): string {
	const formatted = price != null ? formatPrice(price) : '—'
	return template.replace(/\{price\}/g, formatted)
}

export const ProgramCardFooter: React.FC<IProgramCardFooterProps> = ({
	programId: _programId,
	href,
	color = THEME_COLORS_ENUM.CYCLAMEN,
	price,
	oldPrice,
	icon,
	showOrderArrow = true,
	link: linkComponent,
	orderButtonLabel,
	modulesHref
}) => {
	const label = useMemo(() => {
		const customLabel =
			orderButtonLabel != null ? formatOrderLabel(orderButtonLabel, price) : undefined

		const defaultLabel =
			price != null
				? oldPrice != null
					? `Objednať (${formatPrice(price)} <span class="m-program-card__footer-old-price" style="text-decoration: line-through">${formatPrice(oldPrice)}</span>)`
					: `Objednať program (${formatPrice(price)})`
				: 'Objednať program'

		return {
			desktop: customLabel && customLabel.length > 0 ? customLabel : defaultLabel,
			mobile: defaultLabel
		}
	}, [orderButtonLabel, price, oldPrice])

	const showIcon = showOrderArrow && icon != null && icon !== ''
	const showModulesButton = modulesHref != null && modulesHref !== ''

	return (
		<div className='m-program-card__footer'>
			{href != null && href !== '' && (
				<MButton
					href={href}
					color={color}
					title='Objednať'
					icon={showIcon ? M_BUTTON_ARROW_ENUM.RIGHT : undefined}
					iconsSpriteUrl={showIcon ? icon : undefined}
					linkComponent={linkComponent}
				>
					<span className='desktop' dangerouslySetInnerHTML={{ __html: label.desktop }} />
					<span className='mobile' dangerouslySetInnerHTML={{ __html: label.mobile }} />
				</MButton>
			)}
			{showModulesButton && (
				<MButton
					href={modulesHref}
					color={color}
					variant={M_BUTTON_VARIANT_ENUM.OUTLINE}
					linkComponent={linkComponent}
					title='Pridať voliteľné moduly'
					data-scroll-to={
						modulesHref.startsWith('#') ? modulesHref.replace('#', '') : undefined
					}
				>
					Pridať voliteľné moduly
				</MButton>
			)}
		</div>
	)
}

ProgramCardFooter.displayName = 'AProgramCardFooter'
